import {
  Bell,
  CheckCircle,
  ChevronRight,
  Eye,
  Mail,
  Printer,
  Trash2,
  RefreshCw,
  PrinterCheck,
  BellRing,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CfLoader from "../../common/CfLoader/CfLoader";
import DraftsRevokeModal from "../../common/DraftsRevokeModal/DraftsRevokeModal";
import ProgressBar from "../../common/ProgressBar/ProgressBar";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import PendingIcon from "../../resources/icons/alert-triangle.svg";
import { ReactComponent as FlyingMail } from "../../resources/icons/flying-mail.svg";
import podcastIcon from "../../resources/icons/podcast-icon.svg";
import CorrectIcon from "../../resources/icons/succuss-tick.svg";
import { GET, POST } from "../../service/CRUDService";
import useNavigationStore from "../../store/navigationStore";
import MultiSectionModal from "../../common/MultiSectionModal/MultiSectionModal";
import CertificatePreviewModal from "../NotifyRecipients/CertificatePreviewModal";
import useSearchDebounce from "../../hooks/useSearchDebounce";

const ISSUANCE_STATUS_POLLING_INTERVAL = 2000;
const RECIPIENTS_POLLING_INTERVAL = 5000;

const fetchPendingIssuanceTransactions = async ({
  issuerCredentialTemplateId,
  limit = 10,
  offset = 0,
}) => {
  if (!issuerCredentialTemplateId)
    return { error: "Missing issuerCredentialTemplateId" };

  try {
    const response = await GET(
      `${EntityConstants.GET_PENDING_ISSUANCE}?issuer_template_id=${issuerCredentialTemplateId}&limit=${limit}&offset=${offset}`
    );
    const data = await response.json();

    if (data.error) {
      return { error: data.error };
    }

    return {
      results: data.data.results,
      next: data.data.next,
      previous: data.data.previous,
    };
  } catch (error) {
    console.error("Error fetching transactions:", error);
    return { error: "Failed to fetch transactions" };
  }
};

export default function LegacyPendingIssuancePage() {
  const { credentialTemplateId, issuer_credential_template_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const [transactions, setTransactions] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecipientsLoading, setIsRecipientsLoading] = useState(true);
  const [recipientsIssuedCount, setRecipientsIssuedCount] = useState(0);
  const [recipientsNotifiedCount, setRecipientsNotifiedCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [recipientsOffset, setRecipientsOffset] = useState(0);
  const limit = 10;
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [recipientsPrevious, setRecipientsPrevious] = useState(null);
  const [recipientsNext, setRecipientsNext] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const templateType = queryParams.get("templateType");
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState("");
  const [selectedPrintUrl, setSelectedPrintUrl] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [
    isDeletePendingIssuanceModalOpen,
    setIsDeletePendingIssuanceModalOpen,
  ] = useState(false);
  const [isDeleteRecipientsModalOpen, setIsDeleteRecipientsModalOpen] =
    useState(false);
  const [pendingIssuanceToDelete, setPendingIssuanceToDelete] = useState(null);
  const [rowLoadingMap, setRowLoadingMap] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const pollingIntervals = useRef({});
  const recipientsPollingInterval = useRef(null);
  const navigationState = location.state || {};
  const [activeRecipientTab, setActiveRecipientTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const performSearchAPI = async (
    searchTerm,
    signal,
    offset = recipientsOffset
  ) => {
    try {
      if (!issuer_credential_template_id) {
        console.warn("Issuer credential template ID missing during search.");
        setRecipients([]);
        return;
      }

      const transactionId = navigationState.transaction_id;
      let url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${offset}&search=${searchTerm}`;

      if (activeRecipientTab === "issued") {
        url += "&status=Issued,Printed,Notified";
      } else if (activeRecipientTab === "failed") {
        url += "&status=Failed";
      }

      if (transactionId) {
        url += `&transaction_id=${transactionId}`;
      }

      const response = await GET(url, { signal });
      const data = await response.json();

      if (data.error) {
        console.log(data.error);
        setRecipients([]);
      } else {
        setRecipients(data.results);
        setRecipientsPrevious(data.previous);
        setRecipientsNext(data.next);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error during search:", error);
        setRecipients([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const handleSearchInputChange = (e) => {
    const term = e.target.value ? e.target.value : "";
    setSearchTerm(term);

    if (term === "") {
      setRecipientsOffset(0);
      fetchRecipients("", false);
      return;
    }

    if (!issuer_credential_template_id) {
      console.warn("Issuer credential template ID missing during search.");
      setRecipients([]);
      return;
    }

    setRecipientsOffset(0);

    handleSearch(term, {
      onStart: () => {
        setIsRecipientsLoading(true);
      },
      onComplete: () => setIsRecipientsLoading(false),
      onError: () => {
        setRecipients([]);
        setIsRecipientsLoading(false);
      },
      minChars: 1,
    });
  };

  const isDeletionDisabled = useMemo(() => {
    if (selectedRecipientIds.length === 0) {
      return true;
    }
    const selected = recipients.filter((r) =>
      selectedRecipientIds.includes(r.credential_id)
    );
    const allowedStatuses = ["Issued", "Failed", "Revoked"];

    return selected.some((r) => !allowedStatuses.includes(r.status));
  }, [recipients, selectedRecipientIds]);

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      if (location.state?.programme && location.state?.batch) {
        useNavigationStore.setState({
          currentProgramme: location.state.programme,
          currentBatch: location.state.batch,
        });
      } else {
        navigate("/issuer/workspace", { replace: true });
      }
    }
  }, [currentProgramme, currentBatch, navigate, location.state]);

  useEffect(() => {
    if (!Array.isArray(transactions) || transactions.length === 0 || isLoading)
      return;

    transactions.forEach((txn) => {
      if (
        txn.status === "In Progress" &&
        !pollingIntervals.current[txn.legacy_transaction_id]
      ) {
        setRowLoadingMap((prev) => ({
          ...prev,
          [txn.legacy_transaction_id]: true,
        }));

        pollingIntervals.current[txn.legacy_transaction_id] = setInterval(
          async () => {
            try {
              const res = await GET(
                `${EntityConstants.GET_ISSUANCE_STATUS}?legacy_transaction_id=${txn.legacy_transaction_id}`
              );
              const data = await res.json();

              setTransactions((prevTxns) =>
                prevTxns.map((t) =>
                  t.legacy_transaction_id === txn.legacy_transaction_id
                    ? { ...t, issuance_status: data }
                    : t
                )
              );

              if (
                data.transaction_status === "Failed" ||
                data.total_recipients - data.issued - data.failed <= 0
              ) {
                clearInterval(
                  pollingIntervals.current[txn.legacy_transaction_id]
                );
                delete pollingIntervals.current[txn.legacy_transaction_id];

                if (data.transaction_status === "Issued") {
                  Toast({
                    type: "success",
                    title: "Success",
                    message: `Issuance has been successfully completed.`,
                    persistent: true,
                  });
                } else if (data.transaction_status === "Failed") {
                  Toast({
                    type: "error",
                    title: "Issuance Failed",
                    message: "The certificate issuance process has failed.",
                    persistent: true,
                  });
                } else {
                  Toast({
                    type: "error",
                    title: "Issuance Failed",
                    message: "Some thing went wrong.",
                    persistent: true,
                  });
                }

                if (data.transaction_status !== "Failed") {
                  await Promise.all([
                    fetchTransactions(true),
                    fetchRecipients(),
                    fetchRecipientStats(),
                  ]);
                }

                setRowLoadingMap((prev) => ({
                  ...prev,
                  [txn.legacy_transaction_id]: false,
                }));
              }
            } catch (error) {
              Toast({
                type: "error",
                title: "Error",
                message: error?.message || "Failed to fetch transaction status",
              });
            }
          },
          ISSUANCE_STATUS_POLLING_INTERVAL
        );
      }
    });

    Object.keys(pollingIntervals.current).forEach((id) => {
      const txn = transactions.find(
        (t) => t.legacy_transaction_id === id && t.status === "In Progress"
      );
      if (!txn) {
        clearInterval(pollingIntervals.current[id]);
        delete pollingIntervals.current[id];
        setRowLoadingMap((prev) => ({
          ...prev,
          [id]: false,
        }));
      }
    });

    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
      pollingIntervals.current = {};
    };
  }, [isLoading, transactions]);

  useEffect(() => {
    if (!templateType) {
      const fallbackTemplateType =
        localStorage.getItem("templateType") || "Legacy";
      queryParams.set("templateType", fallbackTemplateType);
      navigate(`${location.pathname}?${queryParams.toString()}`, {
        replace: true,
      });
      if (fallbackTemplateType === "Legacy") {
        fetchTransactions();
      }
    } else if (templateType === "Legacy") {
      fetchTransactions();
    }
  }, [
    templateType,
    issuer_credential_template_id,
    offset,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (issuer_credential_template_id) {
      if (!searchTerm) {
        fetchRecipients("", false);
      }
    }
  }, [issuer_credential_template_id, recipientsOffset, activeRecipientTab]);

  useEffect(() => {
    if (issuer_credential_template_id) {
      fetchRecipientStats();
    }
  }, [issuer_credential_template_id]);

  const handleDeletePendingIssuance = (transactionId) => {
    setPendingIssuanceToDelete(transactionId);
    setIsDeletePendingIssuanceModalOpen(true);
  };

  const handleConfirmDeletePendingIssuance = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await POST(EntityConstants.DISCARD_PENDING_ISSUANCE, {
        legacy_transaction_id: pendingIssuanceToDelete,
      });

      if (response.ok) {
        Toast({
          type: "success",
          title: "Success",
          message: "Pending issuance deleted successfully",
        });
        await fetchTransactions();
      } else {
        const data = await response.json();
        Toast({
          type: "error",
          title: "Error",
          message: data.error || "Failed to delete pending issuance",
        });
      }
    } catch (error) {
      console.error("Error deleting pending issuance:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to delete pending issuance",
      });
    } finally {
      setIsDeleteLoading(false);
      setPendingIssuanceToDelete(null);
      setIsDeletePendingIssuanceModalOpen(false);
    }
  };

  const handleDeleteRecipients = () => {
    if (selectedRecipientIds.length === 0) {
      Toast({
        type: "error",
        title: "No Recipients Selected",
        message: "Please select at least one recipient to delete.",
      });
      return;
    }

    const selectedRecipients = recipients.filter((r) =>
      selectedRecipientIds.includes(r.credential_id)
    );

    const allowedStatuses = ["Issued", "Failed", "Revoked"];
    const allAreAllowed = selectedRecipients.every((r) =>
      allowedStatuses.includes(r.status)
    );

    if (allAreAllowed) {
      setIsDeleteRecipientsModalOpen(true);
    }
  };

  const handleConfirmDeleteRecipients = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await POST(EntityConstants.DELETE_RECIPIENTS, {
        credential_ids: selectedRecipientIds,
      });

      const data = await response.json();

      if (response.ok) {
        if (
          data.message &&
          data.message.includes("0 credential(s) deleted") &&
          data.skipped_details &&
          data.skipped_details.length > 0
        ) {
          Toast({
            type: "error",
            title: "Cannot Delete",
            message:
              "The selected certificates cannot be deleted as they have already been notified. Please revoke them instead.",
            persistent: true,
          });
        } else if (data.deleted_ids && data.deleted_ids.length > 0) {
          Toast({
            type: "success",
            title: "Success",
            message: "Recipients deleted successfully",
          });
        } else {
          Toast({
            type: "warning",
            title: "No Action",
            message: "No recipients were deleted. Please check and try again.",
          });
        }

        await fetchRecipientStats();
        await fetchRecipients();
        setSelectedRecipientIds([]);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.error || "Failed to delete recipients",
        });
      }
    } catch (error) {
      console.error("Error deleting recipients:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to delete recipients",
      });
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteRecipientsModalOpen(false);
    }
  };

  const fetchRecipientStats = async () => {
    if (!issuer_credential_template_id) return;
    try {
      const response = await GET(
        `${EntityConstants.GET_RECIPIENT_STATS}/${issuer_credential_template_id}`
      );
      const data = await response.json();
      if (data.error) {
        Toast({
          type: "error",
          title: "Error",
          message: data.error,
        });
      } else {
        setRecipientsIssuedCount(data.data.total_recipient);
        setRecipientsNotifiedCount(data.data.notified_recipient);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch transactions",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactions = async (silent = false) => {
    if (!silent) setIsLoading(true);
    const result = await fetchPendingIssuanceTransactions({
      issuerCredentialTemplateId: issuer_credential_template_id,
      limit,
      offset,
    });

    if (result.error) {
      Toast({
        type: "error",
        title: "Error",
        message: result.error,
      });
    } else {
      setTransactions(result.results);
      setNext(result.next);
      setPrevious(result.previous);
    }

    if (!silent) setIsLoading(false);
  };

  const fetchRecipients = async (searchedTerm, isManual = false) => {
    if (!issuer_credential_template_id) return;
    if (isManual || recipients.length === 0) {
      setIsRecipientsLoading(true);
    }
    if (isManual) {
      setIsManualRefresh(true);
    }
    const transactionId = navigationState.transaction_id;
    let url;
    if (searchedTerm) {
      url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${recipientsOffset}&search=${searchedTerm}`;
    } else {
      url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${recipientsOffset}`;
    }

    if (activeRecipientTab === "issued") {
      url += "&status=Issued,Printed,Notified";
    } else if (activeRecipientTab === "failed") {
      url += "&status=Failed";
    }

    if (transactionId) {
      url += `&transaction_id=${transactionId}`;
    }
    try {
      const response = await GET(url);
      const data = await response.json();
      if (data.error) {
        Toast({
          type: "error",
          title: "Error",
          message: data.error,
        });
      } else {
        setRecipients(data.results);
        setRecipientsPrevious(data.previous);
        setRecipientsNext(data.next);

        if (data.transaction_status === "In Progress") {
          setIsPolling(true);
        } else {
          setIsPolling(false);
          localStorage.removeItem("transaction_id");
        }
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch recipients",
      });
    } finally {
      setIsRecipientsLoading(false);
      setIsManualRefresh(false);
    }
  };

  const handleManualRefresh = () => {
    fetchRecipients(null, true);
    fetchRecipientStats();
  };

  useEffect(() => {
    if (!issuer_credential_template_id) return;

    const pollRecipients = async () => {
      try {
        const transactionId = navigationState.transaction_id;
        let url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${recipientsOffset}`;

        if (activeRecipientTab === "issued") {
          url += "&status=Issued,Printed,Notified";
        } else if (activeRecipientTab === "failed") {
          url += "&status=Failed";
        }

        if (transactionId) {
          url += `&transaction_id=${transactionId}`;
        }

        const response = await GET(url);
        const data = await response.json();

        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
          return;
        }

        setRecipients(data.results);
        setRecipientsPrevious(data.previous);
        setRecipientsNext(data.next);

        if (data.transaction_status !== "In Progress") {
          clearInterval(recipientsPollingInterval.current);
          recipientsPollingInterval.current = null;
          setIsPolling(false);
          localStorage.removeItem("transaction_id");
          await fetchRecipientStats();

          if (data.transaction_status === "Completed") {
            Toast({
              type: "success",
              title: "Success",
              message: `Issuance has been successfully completed.`,
              persistent: true,
            });
          } else if (data.transaction_status === "Failed") {
            Toast({
              type: "error",
              title: "Issuance Failed",
              message: "The certificate issuance process has failed.",
              persistent: true,
            });
          } else {
            Toast({
              type: "error",
              title: "Error",
              message: "Something went wrong.",
            });
          }
        }
      } catch (error) {
        console.error("Error polling recipients:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch recipients",
        });
      }
    };

    if (isPolling && recipientsPollingInterval.current === null) {
      pollRecipients();
      recipientsPollingInterval.current = setInterval(
        pollRecipients,
        RECIPIENTS_POLLING_INTERVAL
      );
    }

    return () => {
      if (recipientsPollingInterval.current) {
        clearInterval(recipientsPollingInterval.current);
        recipientsPollingInterval.current = null;
      }
    };
  }, [
    issuer_credential_template_id,
    recipientsOffset,
    isPolling,
    activeRecipientTab,
  ]);

  const handleSelectAllRecipients = (checked) => {
    if (checked) {
      setSelectedRecipientIds(recipients.map((r) => r.credential_id));
    } else {
      setSelectedRecipientIds([]);
    }
  };

  const handleSelectRecipient = (credentialId, checked) => {
    if (checked) {
      setSelectedRecipientIds((prev) => [...prev, credentialId]);
    } else {
      setSelectedRecipientIds((prev) =>
        prev.filter((id) => id !== credentialId)
      );
    }
  };

  const getNextStepText = (currentStep) => {
    switch (currentStep) {
      case "ZIP Uploaded":
        return "Step 2 - Upload recipients data";
      case "Validated":
      case "Issue":
        return "Issuance Summary Page";
      default:
        return currentStep;
    }
  };

  const getResumeUrl = (transaction) => {
    if (transaction.status === "ZIP Uploaded") {
      return {
        pathname: `/issuer/workspace/batches/legacy-issuance/${transaction.issuer_credential_template_id}`,
        state: {
          transactionData: transaction,
          fromResume: true,
        },
      };
    } else if (
      transaction.status === "Validated" ||
      transaction.status === "Issue"
    ) {
      return `/issuer/workspace/batches/legacy-summary/${transaction.legacy_transaction_id}/${transaction.issuer_credential_template_id}`;
    }
    return `/issuer/workspace/batches/legacy-issuance/${transaction.legacy_transaction_id}`;
  };

  const handleTemplatesClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace`);
  };

  const handlePdfPrint = () => {
    localStorage.setItem(
      "issuerCredentialTemplateId",
      issuer_credential_template_id
    );
    localStorage.setItem("credentialTemplateId", credentialTemplateId);
    navigate(`/issuer/credentials/${issuer_credential_template_id}/print`, {
      state: { issuerCredentialTemplateId: issuer_credential_template_id },
    });
  };

  const handleNotifyRecipients = () => {
    localStorage.setItem(
      "issuerCredentialTemplateId",
      issuer_credential_template_id
    );
    localStorage.setItem("credentialTemplateId", credentialTemplateId);
    localStorage.setItem("templateType", templateType);
    navigate(
      `/issuer/credentials/${credentialTemplateId}/${issuer_credential_template_id}/notify`
    );
  };

  const handlePreview = useCallback(
    (e, previewUrl, printPreviewUrl, recipient) => {
      e.stopPropagation();
      setIsCertificateModalOpen(true);
      setIsLoadingPreview(false);
      setSelectedPreviewUrl(previewUrl);
      setSelectedPrintUrl(printPreviewUrl);
      setSelectedRecipient(recipient);
    },
    []
  );

  const handleIssueNavigate = () => {
    if (templateType === "Legacy") {
      navigate(
        `/issuer/workspace/batches/legacy-issuance/${issuer_credential_template_id}`
      );
    } else {
      navigate(
        `/issuer/certificate/issue/${credentialTemplateId}/${issuer_credential_template_id}`
      );
    }
  };

  const formatHeaderText = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toUpperCase();
  };

  const handleTabChange = (tab) => {
    setActiveRecipientTab(tab);
    setRecipientsOffset(0);
    setSelectedRecipientIds([]);
    setSearchTerm("");
  };

  if (!currentProgramme || !currentBatch) {
    return (
      <div className="w-full mt-20 bg-cfSecondary min-h-screen flex justify-center items-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-20 bg-cfSecondary w-full">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full`}
        >
          <div className="">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <button
                    onClick={handleProgrammeClick}
                    className="hover:text-gray-800 cursor-pointer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleProgrammeClick();
                      }
                    }}
                    tabIndex={0}
                  >
                    {currentProgramme.programme_name || "Programme"}
                  </button>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <button
                    onClick={handleBatchClick}
                    className="hover:text-gray-800 cursor-pointer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleBatchClick();
                      }
                    }}
                    tabIndex={0}
                  >
                    {currentBatch.batch_name || "Batch"}
                  </button>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <button
                    onClick={handleTemplatesClick}
                    className="hover:text-gray-800 cursor-pointer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleTemplatesClick();
                      }
                    }}
                    tabIndex={0}
                  >
                    Certificates
                  </button>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-800 font-medium">Summary</span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto p-4">
        <>
          <div className="max-w-5xl mx-auto">
            {/* Stat Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      Recipients Issued
                    </p>
                    <h3 className="text-3xl font-bold">
                      {recipientsIssuedCount}
                    </h3>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="border rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      Recipients Notified
                    </p>
                    <h3 className="text-3xl font-bold">
                      {recipientsNotifiedCount}
                    </h3>
                  </div>
                  <div className="bg-indigo-100 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Button Group */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 justify-center w-full max-w-[60rem] mx-auto mt-6">
              <button
                className={`min-w-max whitespace-nowrap flex items-center justify-center gap-2 border border-gray-300 px-4 py-2 rounded-[8px] text-gray-800 font-medium ${
                  recipientsIssuedCount === 0
                    ? "opacity-70 cursor-not-allowed"
                    : "cursor-pointer hover:border-[#3538CD] hover:bg-[#3538CD] transition-all duration-300 hover:text-white"
                }`}
                onClick={handlePdfPrint}
                disabled={recipientsIssuedCount === 0}
              >
                <Printer size={18} />
                Print Certificates
              </button>

              <button
                className={`min-w-max whitespace-nowrap flex items-center justify-center gap-2 border border-gray-300 px-4 py-2 rounded-[8px] text-gray-800 font-medium ${
                  recipientsIssuedCount === 0
                    ? "opacity-70 cursor-not-allowed"
                    : "cursor-pointer hover:border-[#3538CD] hover:bg-[#3538CD] transition-all duration-300 hover:text-white"
                } `}
                onClick={handleNotifyRecipients}
                disabled={recipientsIssuedCount === 0}
              >
                <Bell size={18} />
                Notify Recipients
              </button>

              <button
                className={`min-w-max whitespace-nowrap flex items-center justify-center gap-3 border border-gray-300 px-4 py-2 rounded-[8px] font-medium cursor-pointer transition-all duration-300 focus:outline-none
                ${
                  recipients.length === 0
                    ? "bg-[#3538CD] text-white"
                    : "bg-white text-gray-800"
                }
                 hover:!bg-[#3538CD] hover:!text-white hover:!border-[#3538CD]"
              `}
                onClick={handleIssueNavigate}
              >
                <FlyingMail className="h-4 w-4" />
                Issue
              </button>
            </div>
          </div>

          <div className="my-6">
            <h2 className="text-xl font-semibold">Recipients</h2>
            <div className="my-4">
              <div className="flex justify-between items-center">
                <div className="relative flex-1 border-b border-gray-200">
                  <div className="">
                    <button
                      onClick={() => handleTabChange("all")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none ${
                        activeRecipientTab === "all"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      All Recipients
                    </button>
                    <button
                      onClick={() => handleTabChange("issued")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none ${
                        activeRecipientTab === "issued"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      Issued
                    </button>
                    <button
                      onClick={() => handleTabChange("failed")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none ${
                        activeRecipientTab === "failed"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      Failed
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center gap-4 pt-4">
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className={`bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center md:self-end md:m-0 border border-gray-400`}
                >
                  <input
                    type="text"
                    name="search"
                    id="search"
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    placeholder="Search Recipients"
                    className="outline-none border-none p-0 placeholder:text-sm w-full"
                    style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  />
                </form>
                <div className="flex items-center gap-2">
                  {selectedRecipientIds.length > 0 && (
                    <div
                      className="relative inline-block"
                      onMouseEnter={() => {
                        if (isDeletionDisabled) {
                          setShowDeleteMessage(true);
                        }
                      }}
                      onMouseLeave={() => setShowDeleteMessage(false)}
                    >
                      <button
                        onClick={handleDeleteRecipients}
                        disabled={isDeleteLoading || isDeletionDisabled}
                        className={`flex items-center gap-2 rounded-[8px] px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                          isDeleteLoading || isDeletionDisabled
                            ? "cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500"
                            : "cursor-pointer border border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700"
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                        {isDeleteLoading ? "Deleting..." : "Delete"}
                      </button>

                      {showDeleteMessage && (
                        <div className="absolute bottom-full left-1/2 z-10 mb-2 w-max -translate-x-1/2 transform rounded-lg border border-black bg-white px-3 py-2 text-sm font-medium text-black">
                          You can only delete certificates with an
                          <br />
                          &quot;Issued&quot;, &quot;Failed&quot; or
                          &quot;Revoked&quot; status.
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleManualRefresh}
                    className={`flex items-center gap-2 px-3 py-2 rounded-[8px] border border-gray-300 hover:bg-gray-100 ${
                      isManualRefresh ? "cursor-wait" : "cursor-pointer"
                    }`}
                    disabled={isManualRefresh}
                  >
                    <RefreshCw
                      className={`h-4 w-4 ${
                        isManualRefresh || isPolling ? "animate-spin" : ""
                      }`}
                    />
                    <span>Refresh</span>
                  </button>
                </div>
              </div>
            </div>

            {isRecipientsLoading ? (
              <div className="col-span-full w-full flex justify-center items-center py-4">
                <CfLoader />
              </div>
            ) : (
              <>
                {/* Table Rendering */}
                <div className="rounded-lg min-w-full overflow-auto border border-gray-300">
                  <table className="w-full">
                    <thead className="bg-[#e0eaff] h-[3.563rem] text-center">
                      <tr>
                        {recipients.length > 0 && (
                          <th className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={
                                recipients.length > 0 &&
                                selectedRecipientIds.length ===
                                  recipients.length
                              }
                              onChange={(e) =>
                                handleSelectAllRecipients(e.target.checked)
                              }
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                        )}

                        {recipients.length > 0 &&
                          Object.keys(recipients[0])
                            .filter(
                              (key) =>
                                ![
                                  "credential_id",
                                  "credential_digital_preview",
                                  "credential_print_preview",
                                ].includes(key)
                            )
                            .map((key) => (
                              <th
                                key={key}
                                className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {formatHeaderText(key)}
                              </th>
                            ))}
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ACTION
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-center">
                      {recipients.length > 0 ? (
                        recipients.map((recipient) => (
                          <tr
                            key={recipient.credential_id}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedRecipientIds.includes(
                                  recipient.credential_id
                                )}
                                onChange={(e) =>
                                  handleSelectRecipient(
                                    recipient.credential_id,
                                    e.target.checked
                                  )
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            {Object.entries(recipient)
                              .filter(
                                ([key]) =>
                                  ![
                                    "credential_id",
                                    "credential_digital_preview",
                                    "credential_print_preview",
                                  ].includes(key)
                              )
                              .map(([key, value]) => {
                                if (key === "status") {
                                  return (
                                    <td
                                      key={key}
                                      className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                                    >
                                      {value === "Notified" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <BellRing size={16} color="green" />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            Notified
                                          </div>
                                        </div>
                                      ) : value === "Issued" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={CorrectIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            Issued
                                          </div>
                                        </div>
                                      ) : value === "Printed" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <PrinterCheck
                                            size={16}
                                            color="green"
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            Printed
                                          </div>
                                        </div>
                                      ) : value === "Revoked" ? (
                                        <div className="flex items-center justify-center gap-2 bg-red-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={PendingIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusFailedBG text-xs">
                                            Revoked
                                          </div>
                                        </div>
                                      ) : value === "Failed" ? (
                                        <div className="flex items-center justify-center gap-2 bg-red-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={PendingIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusFailedBG text-xs">
                                            Failed
                                          </div>
                                        </div>
                                      ) : value === "Printed_and_Notified" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={CorrectIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            Printed and Notified
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center gap-2 bg-amber-100 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={PendingIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-amber-600 text-xs">
                                            Pending
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                return (
                                  <td
                                    key={key}
                                    className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                                  >
                                    {value || "N/A"}
                                  </td>
                                );
                              })}
                            <td
                              className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center rounded-xl mx-auto w-fit">
                                <button
                                  onClick={(e) =>
                                    handlePreview(
                                      e,
                                      recipient.credential_digital_preview,
                                      recipient.credential_print_preview,
                                      recipient
                                    )
                                  }
                                  disabled={recipient.status === "Failed"}
                                  className={`flex justify-center items-center gap-1 px-3 py-1.5 text-sm font-medium ${
                                    recipient.status === "Failed"
                                      ? "pointer-events-none cursor-not-allowed opacity-50"
                                      : "text-[#3538CD] hover:text-blue-700"
                                  } focus:outline-none`}
                                >
                                  <Eye className="h-4 w-4" />
                                  <span>Preview</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={
                              recipients.length > 0
                                ? Object.keys(recipients[0]).length + 1
                                : 1
                            }
                            className="px-4 py-4 text-md font-semibold text-gray-500 text-center"
                          >
                            No results found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 w-full">
                  <button
                    onClick={() => {
                      setIsRecipientsLoading(true);
                      setRecipientsOffset(
                        Math.max(0, recipientsOffset - limit)
                      );
                    }}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!recipientsPrevious}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                      </svg>
                      <span>previous</span>
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setIsRecipientsLoading(true);
                      setRecipientsOffset(recipientsOffset + limit);
                    }}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!recipientsNext}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>Next</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-between items-center my-4">
            <div className="flex-1">
              {templateType === "Legacy" && (
                <h2 className="text-xl font-semibold">Pending Issuance</h2>
              )}
            </div>
          </div>
          {templateType === "Legacy" &&
            (isLoading ? (
              <div className="col-span-full w-full flex justify-center items-center py-4">
                <CfLoader />
              </div>
            ) : (
              <>
                {Array.isArray(transactions) && transactions.length > 0 ? (
                  <div className="rounded-lg min-w-full overflow-auto border border-gray-300">
                    <table className="w-full">
                      <thead className="bg-[#e0eaff] h-[3.563rem] text-center">
                        <tr>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            Transaction Name
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            Status
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            Progress
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7] whitespace-nowrap">
                            CREATED AT
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7] whitespace-nowrap">
                            UPDATED AT
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            CTA
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            ACTION
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 h-[3.563rem] text-center">
                        {transactions.map((transaction) => (
                          <tr
                            key={transaction.legacy_transaction_id}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm font-medium text-gray-900">
                              {transaction.transaction_name}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              {getNextStepText(transaction.status)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              <div className="flex justify-center items-center w-full">
                                <ProgressBar
                                  status={
                                    transaction.status === "In Progress"
                                      ? "in-progress"
                                      : transaction.issuance_status
                                          ?.transaction_status === "Failed"
                                      ? "failed"
                                      : "completed"
                                  }
                                  success={
                                    transaction.issuance_status?.issued || 0
                                  }
                                  failed={
                                    transaction.issuance_status?.failed || 0
                                  }
                                  total={
                                    transaction.issuance_status
                                      ?.total_recipients || 0
                                  }
                                  showPercentage={Math.round(
                                    ((transaction.issuance_status?.issued ||
                                      0) /
                                      (transaction.issuance_status
                                        ?.total_recipients || 1)) *
                                      100
                                  )}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              {getNextStepText(transaction.created_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              {getNextStepText(transaction.updated_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm items-center">
                              <div className="w-[7rem] mx-auto">
                                <button
                                  disabled={
                                    rowLoadingMap[
                                      transaction.legacy_transaction_id
                                    ]
                                  }
                                  onClick={() => {
                                    if (
                                      !rowLoadingMap[
                                        transaction.legacy_transaction_id
                                      ]
                                    ) {
                                      navigate(getResumeUrl(transaction), {
                                        state: {
                                          transactionData: transaction,
                                          fromResume:
                                            transaction.status ===
                                            "ZIP Uploaded",
                                        },
                                      });
                                    }
                                  }}
                                  className={`border border-gray-500 px-4 py-2 rounded-[8px] text-black font-medium transition-colors duration-300 w-full
                                    ${
                                      rowLoadingMap[
                                        transaction.legacy_transaction_id
                                      ]
                                        ? "bg-gray-200 opacity-60 cursor-not-allowed"
                                        : "hover:bg-gray-100 hover:text-black cursor-pointer"
                                    }`}
                                  style={{ textDecoration: "none" }}
                                >
                                  Resume
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm items-center">
                              <div className="w-[7rem] mx-auto">
                                <button
                                  onClick={() =>
                                    handleDeletePendingIssuance(
                                      transaction.legacy_transaction_id
                                    )
                                  }
                                  className="flex items-center justify-center gap-2 border border-red-500 px-4 py-2 rounded-[8px] text-red-500 font-medium hover:bg-red-50 hover:text-red-700 transition-colors duration-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-col flex-wrap sm:mt-0 xl:mt-6 w-full bg-white min-h-screen rounded-2xl">
                    <div className="flex flex-col px-2 lg:px-0 mt-20 justify-center items-center gap-5">
                      <img
                        alt="certificate-icon"
                        src={podcastIcon}
                        height="80px"
                        width="80px"
                        className=""
                      />
                      <h1 className="text-cfHeadingText font-semibold text-center">
                        No Pending Issuance
                      </h1>
                      <h3 className="text-xs font-bold text-center lg:text-md text-gray-500">
                        You have no pending issuance for this template. Please
                        issue a new one.
                      </h3>
                      <button
                        className="rounded-[8px] items-center bg-[#3538CD] text-white font-medium lg:font-semibold px-5 py-2 text-sm lg:text-md"
                        onClick={handleIssueNavigate}
                      >
                        Issue
                      </button>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 w-full">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!previous}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                      </svg>
                      <span>previous</span>
                    </span>
                  </button>

                  <button
                    onClick={() => setOffset(offset + limit)}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!next}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>Next</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </>
            ))}
        </>
      </div>
      <CertificatePreviewModal
        isOpen={isCertificateModalOpen}
        onClose={() => setIsCertificateModalOpen(false)}
        previewUrl={selectedPreviewUrl}
        printPreviewUrl={selectedPrintUrl}
        isLoading={isLoadingPreview}
        credential_id={selectedRecipient?.credential_id}
      />
      <DraftsRevokeModal
        isOpen={isDeletePendingIssuanceModalOpen}
        onClose={() => {
          setIsDeletePendingIssuanceModalOpen(false);
          setPendingIssuanceToDelete(null);
        }}
        onConfirm={handleConfirmDeletePendingIssuance}
        title="Delete Pending Issuance?"
        description={
          <div className="flex flex-col">
            You are about to delete this pending issuance. This action is
            permanent — once deleted, the issuance cannot be recovered.
            <span className="font-semibold text-black mt-2">
              Are you sure you want to continue?
            </span>
          </div>
        }
        confirmButtonText={isDeleteLoading ? "Deleting..." : "Delete"}
        cancelButtonText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-800"
      />
      <MultiSectionModal
        isOpen={isDeleteRecipientsModalOpen}
        onClose={() => setIsDeleteRecipientsModalOpen(false)}
        onConfirm={handleConfirmDeleteRecipients}
        title={`Delete Certificate${
          selectedRecipientIds.length > 1 ? "s" : ""
        }?`}
        description={
          <div className="flex flex-col">
            {(() => {
              const selectedRecipients = recipients.filter((r) =>
                selectedRecipientIds.includes(r.credential_id)
              );
              const selectionMismatch =
                selectedRecipients.length !== selectedRecipientIds.length;
              const allFailed =
                !selectionMismatch &&
                selectedRecipients.every((r) => r.status === "Failed");
              const allIssued =
                !selectionMismatch &&
                selectedRecipients.every((r) => r.status === "Issued");
              const allRevoked =
                !selectionMismatch &&
                selectedRecipients.every((r) => r.status === "Revoked");

              let statusText = "";
              if (selectionMismatch) {
                statusText = `selected certificate${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else if (allFailed) {
                statusText = `failed certificate${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else if (allIssued) {
                statusText = `issued certificate${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else if (allRevoked) {
                statusText = `revoked certificate${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else {
                // Mixed statuses
                const statusCounts = selectedRecipients.reduce((acc, r) => {
                  acc[r.status] = (acc[r.status] || 0) + 1;
                  return acc;
                }, {});

                const statusList = Object.entries(statusCounts)
                  .map(([status, count]) => `${count} ${status.toLowerCase()}`)
                  .join(", ");

                statusText = `certificate${
                  selectedRecipientIds.length > 1 ? "s" : ""
                } (${statusList})`;
              }

              return (
                <>
                  You are about to delete {selectedRecipientIds.length}{" "}
                  {statusText}. This action is permanent and cannot be undone.{" "}
                  <span className="font-semibold text-black mt-2">
                    Are you sure you want to continue?
                  </span>
                </>
              );
            })()}
          </div>
        }
        confirmButtonText={isDeleteLoading ? "Deleting..." : "Delete"}
        cancelButtonText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-800"
        selectedRecipientIds={selectedRecipientIds}
      />
    </div>
  );
}
